import { create } from 'zustand';
import { api } from '@services/api';

export interface TenantConfig {
  id: string;
  domain: string;
  ramo_atividade: string;
  nome_empresa: string;
  settings?: {
    theme?: 'light' | 'dark';
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    faviconUrl?: string;
    logoType?: 'icon' | 'custom';
  };
  feature_flags?: Record<string, boolean>;
}

interface TenantState {
  tenant: TenantConfig | null;
  isLoading: boolean;
  hasInitialized: boolean;
  error: string | null;
  resolveTenant: (forcedSlug?: string) => Promise<TenantConfig | null>;
}

const SYSTEM_RESERVED_PATHS = new Set([
  'login', 'dashboard', 'ia-chat', 'crm', 'companies', 'chat', 'courses-admin',
  'student-portal', 'marketplace', 'hr', 'finance', 'legal', 'website-designer',
  'webmail', 'settings', 'system-logs', 'reset-password', 'client-portal',
  'portal-cliente', 'portal-cliente-patrimonial', 'portal-cliente-escola',
  'portal-cliente-monitoramento', 'admin-patrimonial', 'admin-escola',
  'admin-monitoramento', 'admin-organica', 'patrimonial', 'organica', 'escola',
  'monitoramento', 'regulamentar', 'abertura', 'tenants', 'diario-oficial',
  'ecossistema', 'sandbox', 'output', 'api', 'uploads', 'whatsapp-admin',
  'profile', 'calendar', 'system', 'collaborators', 'payroll', 'contracts',
  'clientes', 'leads', 'prospeccao', 'campanhas', 'alvaras', 'aprovacao',
  'grade', 'matriculas', 'professores', 'categorias', 'banco-questoes', 'certificados',
  'escalas', 'vitrine', 'checkout', 'public', 'default'
]);

async function syncBrandingWithTenant(tenantData: TenantConfig | null): Promise<void> {
  if (!tenantData?.settings) {
    return;
  }
  const { useBrandingStore } = await import('./brandingStore');
  const currentBranding = useBrandingStore.getState();
  currentBranding.updateBranding({
    systemName: tenantData.nome_empresa || currentBranding.systemName,
    primaryColor: tenantData.settings.primaryColor || currentBranding.primaryColor,
    secondaryColor: tenantData.settings.secondaryColor || currentBranding.secondaryColor,
    logoUrl: tenantData.settings.logoUrl || currentBranding.logoUrl,
    faviconUrl: tenantData.settings.faviconUrl || currentBranding.faviconUrl,
    logoType: tenantData.settings.logoType || currentBranding.logoType,
  });

  const primary = tenantData.settings.primaryColor || '#d4af37';
  const secondary = tenantData.settings.secondaryColor || '#f3e5ab';
  const root = document.documentElement;
  root.style.setProperty('--color-brand-primary', primary);
  root.style.setProperty('--color-brand-secondary', secondary);
  root.style.setProperty('--color-brand-accent', primary);
  root.style.setProperty('--color-brand-gradient', `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`);
  root.style.setProperty('--color-gold-glow', `${primary}59`);
  root.style.setProperty('--shadow-brand', `0 4px 20px ${primary}40`);
  root.style.setProperty('--color-border', `${primary}26`);
}

function clearInvalidTenantStorage(): void {
  try {
    localStorage.removeItem('tenantSlug');
    sessionStorage.removeItem('tenantSlug');
  } catch {
    // Ignora falha de acesso ao storage
  }
}

export const useTenantStore = create<TenantState>((set, get) => ({
  tenant: null,
  isLoading: true,
  hasInitialized: false,
  error: null,
  resolveTenant: async (forcedSlug?: string) => {
    const isInitialBoot = !get().hasInitialized;
    try {
      if (isInitialBoot) {
        set({ isLoading: true, error: null });
      } else {
        set({ error: null });
      }

      // Sanitiza qualquer valor legado incorreto no storage
      const storedSlug = localStorage.getItem('tenantSlug');
      if (storedSlug && SYSTEM_RESERVED_PATHS.has(storedSlug.toLowerCase())) {
        clearInvalidTenantStorage();
      }

      const pathnameFirst = window.location.pathname.split('/').find(Boolean)?.toLowerCase();
      const urlTenantParam = new URLSearchParams(window.location.search).get('tenant');
      const rawCandidate = forcedSlug || 
                           urlTenantParam || 
                           (pathnameFirst && !SYSTEM_RESERVED_PATHS.has(pathnameFirst) ? pathnameFirst : null) || 
                           localStorage.getItem('tenantSlug') || 
                           sessionStorage.getItem('tenantSlug');

      const detectedSlug = rawCandidate && !SYSTEM_RESERVED_PATHS.has(rawCandidate.toLowerCase()) ? rawCandidate : null;
      const isUUID = detectedSlug ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(detectedSlug) : false;

      const queryParam = detectedSlug ? `&tenant=${encodeURIComponent(detectedSlug)}` : '';
      const response = await api.get(
        `/tenant/resolve?t=${Date.now()}${queryParam}`,
        detectedSlug ? { headers: { 'x-tenant-id': detectedSlug } } : { headers: { 'x-tenant-id': 'public' } }
      );
      const tenantData = response.data as TenantConfig;
      const isMaster = !tenantData || tenantData.id === 'public' || tenantData.id === 'master' || tenantData.id === '00000000-0000-0000-0000-000000000000';

      // Só persiste o slug após confirmação 200 OK de um tenant real no backend
      if (detectedSlug && !isUUID && !isMaster) {
        localStorage.setItem('tenantSlug', detectedSlug);
        sessionStorage.setItem('tenantSlug', detectedSlug);
      }

      set({ tenant: tenantData, isLoading: false, hasInitialized: true, error: null });
      await syncBrandingWithTenant(tenantData);

      return tenantData;
    } catch (error: unknown) {
      console.error('Error resolving tenant:', error);
      // Remove imediatamente qualquer slug inexistente/corrompido para evitar envenenamento e loops
      clearInvalidTenantStorage();

      const message = error instanceof Error ? error.message : 'Failed to resolve tenant';

      if (!get().tenant) {
        try {
          const fallbackResponse = await api.get(`/tenant/resolve?t=${Date.now()}`, {
            headers: { 'x-tenant-id': 'public' }
          });
          const fallbackData = fallbackResponse.data as TenantConfig;
          set({ tenant: fallbackData, isLoading: false, hasInitialized: true, error: message });
          await syncBrandingWithTenant(fallbackData);
          return null;
        } catch {
          // Ignora falha no fallback público
        }
      }

      set({ error: message, isLoading: false, hasInitialized: true });
      return null;
    }
  }
}));
